import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import store, { showToast, showToastWithType } from '../core/store';
import { getFormDefinitionById } from '../../util/APIUtils';
import { GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS } from './constants/form-api.constants';
import { normalizeStoredFormSchema } from './utils/form-io.util';
import {
  createNewForm,
  getErrorToastMessage,
  updateExistingForm,
} from './utils/form-manage.util';
import {
  FEEDS_ROUTE,
  FORM_CREATED_SUCCESS,
  FORM_LOAD_ERROR,
  FORM_NAME_REQUIRED_ERROR,
  FORM_UPDATED_SUCCESS,
} from './constants/form-manage.constants';

export const useFormManageHook = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const isEditMode = Boolean(formId);

  const [formName, setFormName] = useState('');
  const [isActive, setIsActive] = useState(true);
  /** Latest Form.io JSON; updated on every builder change and used on submit. */
  const [formSchema, setFormSchema] = useState(() => normalizeStoredFormSchema(undefined));
  /**
   * Seed passed to FormBuilder `initialForm`. Only updated when creating a new form or
   * loading from the API — not on each edit — so the builder is not reset by prop sync.
   */
  const [builderInitialForm, setBuilderInitialForm] = useState(() => normalizeStoredFormSchema(undefined));
  /** Bumps when the editor should remount with a fresh definition (create vs loaded edit). */
  const [schemaEditorKey, setSchemaEditorKey] = useState(0);
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);

  const applyLoadedFormDefinition = useCallback((definition) => {
    const normalized = normalizeStoredFormSchema(definition?.schema);
    setFormName(definition?.name ?? '');
    setIsActive(definition?.active !== false);
    setFormSchema(normalized);
    setBuilderInitialForm(normalized);
    setSchemaEditorKey((k) => k + 1);
  }, []);

  const resetFormForCreateMode = useCallback(() => {
    const blank = normalizeStoredFormSchema(undefined);
    setFormName('');
    setIsActive(true);
    setFormSchema(blank);
    setBuilderInitialForm(blank);
    setSchemaEditorKey((k) => k + 1);
    setPageLoading(false);
  }, []);

  const handleLoadFailure = useCallback(() => {
    store.dispatch(showToast(FORM_LOAD_ERROR));
    navigate(FEEDS_ROUTE);
  }, [navigate]);

  const loadExistingForm = useCallback(
    async (cancelledRef) => {
      setPageLoading(true);
      try {
        const definition = await getFormDefinitionById(formId, GET_FORM_DEFINITION_FORCE_RESPONSE_OPTIONS);
        if (!cancelledRef.current) {
          applyLoadedFormDefinition(definition);
        }
      } catch {
        if (!cancelledRef.current) {
          handleLoadFailure();
        }
      } finally {
        if (!cancelledRef.current) {
          setPageLoading(false);
        }
      }
    },
    [formId, applyLoadedFormDefinition, handleLoadFailure],
  );

  const handleFormNameChange = useCallback(
    (e) => {
      setFormName(e.target.value);
      if (nameError) {
        setNameError('');
      }
    },
    [nameError],
  );

  const handleIsActiveChange = useCallback((_e, checked) => {
    setIsActive(checked);
  }, []);

  const handleFormSchemaChange = useCallback((nextSchema) => {
    setFormSchema(nextSchema);
  }, []);

  const persistFormForSubmit = useCallback(
    async (trimmedName) => {
      const payload = { name: trimmedName, isActive, schema: formSchema };
      if (isEditMode && formId) {
        await updateExistingForm(formId, payload);
        store.dispatch(showToastWithType(FORM_UPDATED_SUCCESS, 'success'));
        return;
      }
      await createNewForm(payload);
      store.dispatch(showToastWithType(FORM_CREATED_SUCCESS, 'success'));
    },
    [isEditMode, formId, isActive, formSchema],
  );

  const submitFormChanges = useCallback(
    async (trimmedName) => {
      setNameError('');
      setSubmitting(true);
      try {
        await persistFormForSubmit(trimmedName);
        navigate(FEEDS_ROUTE);
      } catch (err) {
        const message = await getErrorToastMessage(err);
        store.dispatch(showToast(message));
      } finally {
        setSubmitting(false);
      }
    },
    [persistFormForSubmit, navigate],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const trimmed = formName.trim();
      if (trimmed === '') {
        setNameError(FORM_NAME_REQUIRED_ERROR);
        return;
      }
      await submitFormChanges(trimmed);
    },
    [formName, submitFormChanges],
  );

  const handleBackToFeeds = useCallback(() => {
    navigate(FEEDS_ROUTE);
  }, [navigate]);

  const isBusy = submitting || pageLoading;

  useEffect(() => {
    if (!formId) {
      resetFormForCreateMode();
      return undefined;
    }
    const cancelledRef = { current: false };
    void loadExistingForm(cancelledRef);
    return () => {
      cancelledRef.current = true;
    };
  }, [formId, resetFormForCreateMode, loadExistingForm]);

  return {
    formName,
    isActive,
    formSchema,
    builderInitialForm,
    schemaEditorKey,
    nameError,
    submitting,
    pageLoading,
    isBusy,
    isEditMode,
    handleFormNameChange,
    handleIsActiveChange,
    handleFormSchemaChange,
    handleSubmit,
    handleBackToFeeds,
  };
};
